import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserX, MessageSquareWarning, MessageCircle } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { ReasonDialog } from "@/components/BookingStatus";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDay } from "@/lib/time";
import type { CaseItem } from "@/types";
import { useLive } from "@/lib/live";

const waNum = (p: string) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("07")) d = "964" + d.slice(1);
  return d;
};

const CaseCard = ({ c, onDecide }: { c: CaseItem; onDecide: (c: CaseItem, approve: boolean) => void }) => {
  const { t, lang } = usePrefs();
  const open = c.status === "open" || c.status === "awaiting_admin";
  const ns = c.kind === "no_show";
  const state = c.status === "open" && c.messages.length === 0 ? "waitingCustomer" : c.status;
  const ev = lang === "ar" ? c.event_title_ar : c.event_title_en;
  const last = c.messages[c.messages.length - 1];
  const msg = t("cx.waMsg").replace("{name}", c.customer_name || "").replace("{event}", ev || "").replace("{date}", fmtDay(c.date, lang));
  const btn = "h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1.5";
  return (
    <div data-testid={`case-${c.case_id}`} className={`rounded-2xl border bg-card p-3 space-y-2 ${open ? "border-s-4 border-s-amber-400" : ""}`}>
      <div className="flex items-center gap-2">
        {ns ? <UserX className="h-4 w-4 text-rose-500 shrink-0" /> : <MessageSquareWarning className="h-4 w-4 text-sky-500 shrink-0" />}
        <p className="font-bold text-sm truncate flex-1" data-testid={`case-customer-${c.case_id}`}>{c.customer_name}</p>
        <span data-testid={`case-status-${c.case_id}`} className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold shrink-0">{t(`cx.short.${state}`)}</span>
      </div>
      <p className="text-xs text-muted-foreground truncate">{t(ns ? "cx.missed" : "b6.kind.dispute")}: {ev} · {fmtDay(c.date, lang)}</p>
      {last && <p dir="auto" className="text-xs rounded-xl bg-accent/60 px-2.5 py-1.5 line-clamp-2">{lang === "ar" ? last.text_ar || last.text : last.text}</p>}
      {c.decision && <p className="text-xs text-muted-foreground truncate">{t("b6.decision")}: {c.decision.reason}</p>}
      <div className="flex flex-wrap gap-1.5">
        {c.customer_phone && (
          <a href={`https://wa.me/${waNum(c.customer_phone)}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid={`case-whatsapp-${c.case_id}`} className={`${btn} bg-[#25D366] text-white`}>
            <MessageCircle className="h-3.5 w-3.5" />{t("cx.wa")}
          </a>
        )}
        {open && <>
          <button data-testid={`case-approve-${c.case_id}`} onClick={() => onDecide(c, true)} title={t(ns ? "cx.liftHint" : "cx.acceptHint")} className={`${btn} bg-emerald-600 text-white`}>{t(ns ? "cx.lift" : "b6.approveDispute")}</button>
          <button data-testid={`case-reject-${c.case_id}`} onClick={() => onDecide(c, false)} title={t(ns ? "cx.keepHint" : "cx.rejectHint")} className={`${btn} border border-primary/40 text-primary`}>{t(ns ? "cx.keep" : "cx.rejectDispute")}</button>
        </>}
      </div>
    </div>
  );
};

export default function AdminCases() {
  const { t } = usePrefs();
  const [tab, setTab] = useState<"active" | "closed">("active");
  const [list, setList] = useState<CaseItem[] | null>(null);
  const [deciding, setDeciding] = useState<{ c: CaseItem; approve: boolean } | null>(null);
  const load = () => api.get("/admin/cases").then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => { load(); }, []);
  const decide = async (reason: string) => {
    try {
      await api.post(`/admin/cases/${deciding!.c.case_id}/decide`, { approve: deciding!.approve, reason });
      toast.success(t("b6.decided"));
      setDeciding(null);
      load();
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };
  const shown = list?.filter((c) => (tab === "active") === ["open", "awaiting_admin"].includes(c.status));
  return (
    <div data-testid="admin-cases-page">
      <PageTitle title={t("b6.cases")} sub={t("b6.casesSub")} />
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {(["active", "closed"] as const).map((k) => (
          <button key={k} data-testid={`cases-tab-${k}`} onClick={() => setTab(k)} aria-pressed={tab === k}
            className={`h-10 px-5 rounded-full text-sm font-bold ${tab === k ? "bg-foreground text-background" : "bg-card border hover:bg-accent"}`}>{t(`b6.${k}Tab`)}</button>
        ))}
      </div>
      {shown?.length === 0 && <p data-testid="cases-empty" className="text-muted-foreground py-10 text-center">{t("b5.empty")}</p>}
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{shown?.map((c) => <CaseCard key={c.case_id} c={c} onDecide={(c2, approve) => setDeciding({ c: c2, approve })} />)}</div>
      {deciding && <ReasonDialog testid="case-decision-dialog" title={t(deciding.approve ? (deciding.c.kind === "no_show" ? "cx.lift" : "b6.approveDispute") : (deciding.c.kind === "no_show" ? "cx.keep" : "cx.rejectDispute"))} danger={!deciding.approve} onClose={() => setDeciding(null)} onSubmit={decide} />}
    </div>
  );
}
